/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

interface CampaignState {
  campaign: BeatlyApi.V2BOCampaign | null;
}

interface CampaignStateContextType {
  campaign: BeatlyApi.V2BOCampaign | null;
  setCampaign: (campaign: BeatlyApi.V2BOCampaign) => void;
  clearCampaign: () => void;
  hasCampaignSelected: boolean;
}

// Generate a unique tab identifier for this session
const generateTabId = (): string => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create tab ID for this session
const getTabId = (): string => {
  const existingTabId = sessionStorage.getItem('beatly-tab-id');
  if (existingTabId) {
    return existingTabId;
  }

  const newTabId = generateTabId();
  sessionStorage.setItem('beatly-tab-id', newTabId);
  return newTabId;
};

// Create the context
const CampaignStateContext = createContext<CampaignStateContextType | undefined>(undefined);

// Provider component
export function CampaignStateProvider({ children }: { children: ReactNode }) {
  const [campaignState, setCampaignState] = useState<CampaignState>({
    campaign: null,
  });

  // Initialize state from sessionStorage on mount (per-tab storage)
  useEffect(() => {
    try {
      const tabId = getTabId();
      const storageKey = `beatly-selected-campaign-${tabId}`;
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Handle backward compatibility - check if it's the old format
        if (parsed.campaign_id && parsed.campaign_name && !parsed.campaign_id) {
          // Old format - we'll need to fetch the full campaign
          console.warn('Found old campaign storage format, campaign will need to be refetched');
          setCampaignState({ campaign: null });
        } else {
          // New format - full campaign object
          setCampaignState({ campaign: parsed });
        }
      }
    } catch (error) {
      console.error('Failed to load campaign state from sessionStorage:', error);
    }
  }, []);

  const setCampaign = useCallback((campaign: BeatlyApi.V2BOCampaign) => {
    setCampaignState((prev) => {
      if (prev.campaign === campaign) {
        return prev;
      }
      try {
        const tabId = getTabId();
        const storageKey = `beatly-selected-campaign-${tabId}`;
        sessionStorage.setItem(storageKey, JSON.stringify(campaign));
      } catch (error) {
        console.error('Failed to save campaign state to sessionStorage:', error);
      }
      return { campaign };
    });
  }, []);

  const clearCampaign = useCallback(() => {
    setCampaignState({ campaign: null });

    try {
      const tabId = getTabId();
      const storageKey = `beatly-selected-campaign-${tabId}`;
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear campaign state from sessionStorage:', error);
    }
  }, []);

  const hasCampaignSelected = campaignState.campaign !== null;

  const value: CampaignStateContextType = useMemo(
    () => ({
      campaign: campaignState.campaign,
      setCampaign,
      clearCampaign,
      hasCampaignSelected,
    }),
    [campaignState.campaign, setCampaign, clearCampaign, hasCampaignSelected],
  );

  return (
    <CampaignStateContext.Provider value={value}>
      {children}
    </CampaignStateContext.Provider>
  );
}

// Hook to use the context
export function useCampaignState() {
  const context = useContext(CampaignStateContext);
  if (context === undefined) {
    throw new Error('useCampaignState must be used within a CampaignStateProvider');
  }
  return context;
}
